import { BASE_URL, ERROR_SCREEN_TYPES } from 'config/config';
import getRouteWithParams from 'helpers/getRouteWithParams';
import queryString from 'query-string';

interface IData {
  message: string;
  screenType?: string;
}

interface IRestError {
  message?: string;
  errorType?: string;
}

interface IRestErrorResponse {
  errors?: IRestError[];
}

class RestService {
  private headers: Record<string, string>;

  private baseUrl: maybe<string>;

  private token = queryString.parse(window.location.search).token || '';

  constructor() {
    this.headers = {
      Accept: 'application/json',
      Authorization: `Bearer ${this.token}`,
      'Client-Referer': document.referrer,
      'Content-Type': 'application/json',
    };
    this.baseUrl = BASE_URL;
  }

  getNotCache(route: string, params?: IUrlParams, uri?: string, fingerprint?: string) {
    const newHeaders = this.headers;
    newHeaders['Cache-Control'] = 'no-cache';
    if (fingerprint) {
      newHeaders['Device-Fingerprint'] = fingerprint;
    }

    return this.get(route, params, uri, newHeaders);
  }

  get(route: string, params?: IUrlParams, uri?: string, headers = this.headers) {
    let newRoute = route;
    if (params) {
      newRoute = getRouteWithParams(route, params);
    }
    return this.xhr(this.getURL(newRoute, uri), params, 'GET', headers);
  }

  post(route: string, params?: IUrlParams, uri?: string) {
    return this.xhr(this.getURL(route, uri), params, 'POST', this.headers);
  }

  patch(route: string, params?: IUrlParams, uri?: string) {
    return this.xhr(this.getURL(route, uri), params, 'PATCH', this.headers);
  }

  delete(route: string, params?: IUrlParams, uri?: string) {
    return this.xhr(this.getURL(route, uri), params, 'DELETE', this.headers);
  }

  getURL(route: string, uri = this.baseUrl) {
    return `${uri}${route}`;
  }

  xhr(url: string, params: maybe<IUrlParams>, method: string, headers: Record<string, string>) {
    if (!this.token) {
      return Promise.reject(new Error('No Token'));
    }

    const options = {
      body: !params || method === 'GET' ? undefined : JSON.stringify(params),
      headers,
      method,
      url,
    };

    return fetch(url, options)
      .then((response: Response) => {
        switch (response.status) {
          case 200:
          case 201:
            return response.json();
          case 204:
            return Promise.resolve();
          case 400: {
            return response.json().then((data: IData) => Promise.reject(data));
          }
          case 401: {
            return response.text().then(data => {
              const json: IRestErrorResponse = data && data.length > 0 ? JSON.parse(data) : {};
              const errorMessage = json.errors && json.errors.length > 0 ? json.errors[0].message : 'UNAUTHORIZED';

              let errorData;
              if (errorMessage === 'TOKEN_EXPIRED') {
                errorData = {
                  message: errorMessage,
                  screenType: ERROR_SCREEN_TYPES.PAYMENT_TOKEN_EXPIRED,
                };
              } else {
                errorData = { message: errorMessage, screenType: ERROR_SCREEN_TYPES.DEFAULT };
              }
              return Promise.reject(errorData);
            });
          }
          case 403: {
            return response.json().then((data: IData) => {
              data.screenType = ERROR_SCREEN_TYPES.AUTHORIZATION;
              return Promise.reject(data);
            });
          }
          case 422: {
            return response.text().then(data => {
              const json: IRestErrorResponse = data && data.length > 0 ? JSON.parse(data) : {};
              const errorMessage =
                json.errors && json.errors.length > 0 ? json.errors[0].message : 'An internal error has occurred';
              const errorType = json.errors && json.errors.length > 0 ? json.errors[0].errorType : 'ERROR';
              let errorData;
              if (errorType === 'MERCHANT_DATA_NOT_PERMITTED_ERROR') {
                errorData = {
                  message: errorMessage,
                  screenType: ERROR_SCREEN_TYPES.NOT_PERMITTED,
                };
              } else {
                errorData = new Error(errorMessage);
              }
              return Promise.reject(errorData);
            });
          }
          default: {
            const message = response.statusText || 'An internal error has occurred';
            return Promise.reject(new Error(message));
          }
        }
      })
      .catch((err: Error) => Promise.reject(err));
  }
}

export default new RestService();
